import React from "react";

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

interface ConfirmDialogProps {
	open: boolean;
	title?: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	confirmColor?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
	confirmDisabled?: boolean;
	cancelDisabled?: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	sx?: object;
	children?: React.ReactNode;
}

/**
 * A reusable confirmation dialog component.
 *
 * Usage examples:
 *
 * // Delete confirmation:
 * <ConfirmDialog
 *   open={showDialog}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item?"
 *   confirmText="Delete"
 *   confirmColor="error"
 *   onConfirm={handleDelete}
 *   onCancel={handleCancel}
 * />
 *
 * // Save confirmation:
 * <ConfirmDialog
 *   open={showDialog}
 *   title="Save Changes"
 *   message="Do you want to save your changes?"
 *   confirmText="Save"
 *   confirmColor="primary"
 *   onConfirm={handleSave}
 *   onCancel={handleCancel}
 * />
 *
 * // Logout confirmation:
 * <ConfirmDialog
 *   open={showDialog}
 *   title="Logout"
 *   message="Are you sure you want to logout?"
 *   confirmText="Logout"
 *   confirmColor="warning"
 *   onConfirm={handleLogout}
 *   onCancel={handleCancel}
 * />
 */
export default function ConfirmDialog({
	open,
	title = "Confirm Action",
	message,
	confirmText = "Confirm",
	cancelText = "Cancel",
	confirmColor = "primary",
	confirmDisabled = false,
	cancelDisabled = false,
	onConfirm,
	onCancel,
	sx,
	children,
}: ConfirmDialogProps) {
	return (
		<Dialog open={open} onClose={cancelDisabled ? undefined : onCancel} maxWidth="sm" fullWidth sx={sx} TransitionProps={{ timeout: { enter: 180, exit: 120 } }}>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<DialogContentText>{message}</DialogContentText>
				{children}
			</DialogContent>
			<DialogActions>
				<Button onClick={onCancel} disabled={cancelDisabled}>{cancelText}</Button>
				<Button onClick={onConfirm} color={confirmColor} variant="contained" disabled={confirmDisabled}>
					{confirmText}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
